import { useCallback, useRef, useState } from 'react';
import { type EmailLinkSignIn, type SignInLinkEmailStore, sendSignInLinkQuietly } from '../../api';
import { type Resend, useResend } from '../use-resend';
import { SIGN_IN_LINK_RESEND_MESSAGES } from './sign-in-link-messages';

const RESEND_OPTIONS = { messages: SIGN_IN_LINK_RESEND_MESSAGES };

export interface SignInLinkRequestOptions {
  emailLink: EmailLinkSignIn;
  emailStore: SignInLinkEmailStore;
  /** Where the link continues to: /sign-in/link on IDENTITY_URL, keeping a valid return_to. */
  continueUrl: string;
}

export interface SignInLinkRequest {
  /** The request's progress, cooldown and error, for the screen it was made from. */
  resend: Resend;
  /** Sends a link to `email` unless one is in flight or cooling down; resolves whether it sent. */
  request: (email: string) => Promise<boolean>;
  /** The address a link was sent to, once one was: time to show SignInLinkSentScreen. */
  sentTo?: string;
  /** SignInLinkSentScreen's "Resend email", which announces each resend. */
  sentScreen: { resend: Resend; showStatus: boolean };
}

/**
 * "Email me a sign-in link": sends quietly (sendSignInLinkQuietly), so every well-formed request looks sent, and
 * remembers the address on this device. Sends at most once per RESEND_COOLDOWN_SECONDS (useResend), and a
 * rate-limited send cools down too.
 */
export const useSignInLinkRequest = ({
  emailLink,
  emailStore,
  continueUrl,
}: SignInLinkRequestOptions): SignInLinkRequest => {
  // The address to send to, read by the send callback on the same tick as the request that sets it.
  const address = useRef('');
  const [sentTo, setSentTo] = useState<string>();
  const [resent, setResent] = useState(false);

  const resend = useResend(
    useCallback(
      () => sendSignInLinkQuietly(emailLink, emailStore, address.current, continueUrl),
      [emailLink, emailStore, continueUrl],
    ),
    RESEND_OPTIONS,
  );

  const request = async (email: string) => {
    address.current = email;
    const sent = await resend.resend();
    if (sent) {
      setSentTo(email);
    }
    return sent;
  };

  const resendFromSent = async () => {
    setResent(true);
    return resend.resend();
  };

  return { resend, request, sentTo, sentScreen: { resend: { ...resend, resend: resendFromSent }, showStatus: resent } };
};
